import React, { useEffect, useMemo, useRef, useState } from 'react'
import Modal from '../../../components/modal/modal.component'
import { useTranslation } from 'react-i18next';
import classes from '../styles.module.scss'
import { createCn } from '../../../helpers/utils';
import { GreenTick, Pdf, XIcon } from '../../../assets';
import VoiceRecord from './voice-record.component';
import { Speaker } from '../speakers.page';
import { toast } from 'react-toastify';
import globalClasses from '../../styles.module.scss'
import { apiService } from '../../../services/api.service';

interface IProps {
  show: boolean;
  handleClose: () => void;
  type: 'edit' | 'create';
  onRefresh: () => void;
  item?: Speaker;
}

const cn = createCn('speakers', classes)

const SpeakerModal = ({ show, handleClose, type, onRefresh, item }: IProps) => {
  const { t } = useTranslation();

  const [createData, setCreateData] = useState({
    name: '',
    position: '',
    voice: null as File | null,
    file: null as File | null,
    duration: '00:00:00',
  });

  const [editData, setEditData] = useState({
    name: '',
    position: '',
    voice: null as File | null,
    file: null as File | null,
    duration: '00:00:00',
  });

  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const voiceFile = file || (type === 'create' ? createData.voice : editData.voice);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      startUpload(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      startUpload(e.dataTransfer.files[0]);
    }
  };

  const startUpload = (selectedFile: File) => {
    setFile(selectedFile);
    setProgress(0);
    setUploading(true);
  };

  const cancelUpload = () => {
    setFile(null);
    setProgress(0);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (isDisabled || !item || saving) return;
    setSaving(true);
    try {
      await apiService.updateSpeaker(item.id, editData.name.trim(), editData.position.trim());
      onRefresh();
      handleClose();
      toast.success(t('speakers.updated'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('speakers.update-error'));
    } finally {
      setSaving(false);
    }
  }

  const isDisabled =
    type === 'edit'
      ? !editData.name.trim() || !editData.position.trim() || saving
      : !createData.name.trim() || !createData.position.trim() || !voiceFile || (uploading && progress < 100) || saving;

  const handleAdd = async () => {
    if (isDisabled || !voiceFile) return;
    setSaving(true);
    try {
      await apiService.createSpeaker(
        createData.name.trim(),
        createData.position.trim(),
        voiceFile,
      );
      onRefresh();
      handleClose();
      toast(
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <GreenTick />
          {t('speakers.success')}
        </div>,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('speakers.add-error'));
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (uploading && file) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setUploading(false);
            return 100;
          }
          return prev + 10;
        });
      }, 300);
      return () => clearInterval(interval);
    }
  }, [uploading, file]);

  useEffect(() => {
    if (type === 'edit' && item) {
      setEditData({
        name: item.name,
        position: item.position,
        voice: item.voice,
        file: item.file || null,
        duration: item.duration,
      });
    }
  }, [type, show, item]);

  useEffect(() => {
    setCreateData({
      name: '',
      position: '',
      voice: null,
      file: null,
      duration: '00:00:00',
    });
    setFile(null);
    setProgress(0);
    setUploading(false);
    setSaving(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [show])

  const header = useMemo(() => {
    if (type === 'edit') {
      return <div className={cn('modal-header')}>
        {t('speakers.modal.edit-heading')}
      </div>
    } else if (type === 'create') {
      return <div className={cn('modal-header')}>
        {t('speakers.modal.create-heading')}
      </div>
    }
  }, [type, t])

  const body = () => {
    return <div className={cn('modal-body')}>
      <div className={globalClasses['form-group']}>
        <label>{t('speakers.fullname')}</label>
        <input
          type="text"
          value={type === 'create' ? createData.name : editData.name}
          onChange={(e) =>
            type === 'create'
              ? setCreateData(prev => ({ ...prev, name: e.target.value }))
              : setEditData(prev => ({ ...prev, name: e.target.value }))
          }
          placeholder={t('speakers.fullname') || ''}
        />
      </div>
      <div className={globalClasses['form-group']}>
        <label>{t('speakers.position')}</label>
        <input
          type="text"
          value={type === 'create' ? createData.position : editData.position}
          onChange={(e) =>
            type === 'create'
              ? setCreateData(prev => ({ ...prev, position: e.target.value }))
              : setEditData(prev => ({ ...prev, position: e.target.value }))
          }
          placeholder={t('speakers.position') || ''}
        />
      </div>

      {type === 'create' && (
        <div className={cn('form-voice')}>
          <VoiceRecord
            value={createData.voice}
            onChange={(voice, duration) => {
              setCreateData(prev => ({ ...prev, voice, duration }));
            }}
          />

          <p>{t('speakers.or')}</p>

          <div
            className={cn('upload-area')}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            style={{ borderColor: file ? 'var(--color-gray-light)' : 'var(--color-primary)' }}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept=".mp3,.wav,.ogg,.m4a,.mp4,.webm,.m4v,.MP4"
            />

            {!file ? (
              <>
                <p>
                  <span
                    className={cn('upload-link')}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t('start-recording.file.modal.upload')}
                  </span>{' '}
                  {t('start-recording.file.modal.upload-text')}
                </p>
                <div className={cn('upload-info')}>
                  <p>{t('start-recording.file.modal.audio-formats')}</p>
                  <p>{t('start-recording.file.modal.video-formats')}</p>
                </div>
              </>
            ) : (
              <div className={cn('file-info')}>
                <div className={cn('file-header')}>
                  <Pdf />
                  <div className={cn('file-name')}>
                    <span>{file.name}</span>
                    <span>
                      {file.size < 1024 * 1024
                        ? `${(file.size / 1024).toFixed(2)} KB`
                        : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    </span>
                  </div>
                  <XIcon onClick={cancelUpload} />
                </div>

                {progress < 100 && (
                  <div className={cn('progress-wrapper')}>
                    <div className={cn('progress-text')} >{progress}%</div>
                    <div className={cn('progress-bar')} style={{ width: `${progress}%` }} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  }

  const footer = () => {
    if (type === 'edit') {
      return <div className={cn('modal-footer')}>
        <button onClick={handleSave} disabled={isDisabled}>
          {saving ? t('speakers.saving') : t('action.save')}
        </button>
      </div>
    } else if (type === 'create') {
      return <div className={cn('modal-footer')}>
        <button onClick={handleAdd} disabled={isDisabled}>
          {saving ? t('speakers.saving') : t('action.add')}
        </button>
      </div>
    }
  }


  return (
    <Modal
      show={show}
      handleClose={handleClose}
      style={{ padding: 0, minHeight: 'auto', borderRadius: '1rem' }}
      footer={footer()}
    >
      <div className={cn('modal')}>
        {header}
        {body()}
      </div>
    </Modal>
  )
}

export default SpeakerModal
